import * as THREE from 'three';
import { detectBeamIntersection, IntersectionPoint } from './BeamIntersectionUtils';

// Type definitions for structural elements
export interface Node3D {
  id: string;
  x: number;
  y: number;
  z: number;
  label: string;
  isSelected: boolean;
  isHovered: boolean;
  supportType: 'free' | 'pinned' | 'roller' | 'fixed';
  restraints: {
    fx: boolean; // X-translation restraint
    fy: boolean; // Y-translation restraint
    fz: boolean; // Z-translation restraint
    mx: boolean; // X-rotation restraint
    my: boolean; // Y-rotation restraint
    mz: boolean; // Z-rotation restraint
  };
  properties?: {
    material?: string;
    section?: string;
    [key: string]: any;
  };
  visualState?: 'normal' | 'preview' | 'highlighted' | 'focused';
}

export interface Beam3D {
  id: string;
  startNodeId: string;
  endNodeId: string;
  section: string;
  material: 'steel' | 'concrete' | 'wood';
  localAxes: {
    x: [number, number, number];
    y: [number, number, number];
    z: [number, number, number];
  };
  betaAngle: number;
  releases: {
    start: { fx: boolean; fy: boolean; fz: boolean; mx: boolean; my: boolean; mz: boolean; };
    end: { fx: boolean; fy: boolean; fz: boolean; mx: boolean; my: boolean; mz: boolean; };
  };
  length?: number;
  isSelected: boolean;
  isHovered: boolean;
  visualState?: 'normal' | 'preview' | 'highlighted' | 'stressed';
  stressLevel?: number;
}

export interface NodeCreationOptions {
  toleranceDistance?: number; // Distance threshold for detecting duplicates (mm)
  autoConnect?: boolean; // Whether to automatically connect to nearby nodes
  connectionDistance?: number; // Maximum distance for auto-connection (mm)
  supportType?: 'free' | 'pinned' | 'roller' | 'fixed';
  snapToGrid?: boolean;
  gridSize?: number;
}

export interface BeamCreationOptions {
  preventDuplicates?: boolean;
  material?: 'steel' | 'concrete' | 'wood';
  section?: string;
  autoReleases?: boolean;
}

export interface StructuralElementResult {
    success: boolean;
    message: string;
    nodeId?: string;
    beamId?: string;
    element?: Node3D | Beam3D;
    duplicateElement?: Node3D | Beam3D;
}

export interface IntersectionNodeResult {
    success: boolean;
    message: string;
    nodeId: string;
    beamId: string;
    newBeams: Beam3D[];
    removedBeamId: string;
}

/**
 * Comprehensive utility class for managing structural elements (nodes and beams)
 * with duplicate prevention and automatic connectivity features.
 * 
 * Features:
 * - Prevents duplicate nodes within tolerance distance
 * - Prevents overlapping beams (same start/end nodes)
 * - Automatically connects new nodes to nearby existing nodes
 * - Provides feedback for all operations
 * - Supports grid snapping
 * - Professional STAAD.Pro-like behavior
 */
export class StructuralElementManager {
  private nodes: Map<string, Node3D> = new Map();
  private beams: Map<string, Beam3D> = new Map();
  private nodeCounter: number = 0;
  private beamCounter: number = 0;
  private defaultOptions: NodeCreationOptions;

  constructor(options?: Partial<NodeCreationOptions>) {
    this.defaultOptions = {
      toleranceDistance: 10, // 10mm tolerance for duplicate detection
      autoConnect: true,
      connectionDistance: 5000, // 5m maximum auto-connection distance
      supportType: 'free',
      snapToGrid: true,
      gridSize: 100, // 100mm grid
      ...options
    };
  }

  /**
   * Updates the internal state with current nodes and beams
   */
  public updateState(nodes: Node3D[], beams: Beam3D[]): void {
    this.nodes.clear();
    this.beams.clear();
    
    nodes.forEach(node => {
      this.nodes.set(node.id, node);
      const nodeNum = parseInt(node.id.replace(/\D/g, ''), 10);
      if (nodeNum > this.nodeCounter) {
        this.nodeCounter = nodeNum;
      }
    });

    beams.forEach(beam => {
      this.beams.set(beam.id, beam);
      const beamNum = parseInt(beam.id.replace(/\D/g, ''), 10);
      if (beamNum > this.beamCounter) {
        this.beamCounter = beamNum;
      }
    });
  }

  /**
   * Creates a node at the specified coordinates with duplicate checking and auto-connectivity
   */
  public createNodeAt(
    x: number, 
    y: number, 
    z: number, 
    options?: Partial<NodeCreationOptions>
  ): StructuralElementResult {
    const opts = { ...this.defaultOptions, ...options };
    
    // Apply grid snapping if enabled
    if (opts.snapToGrid && opts.gridSize) {
      x = Math.round(x / opts.gridSize) * opts.gridSize;
      y = Math.round(y / opts.gridSize) * opts.gridSize;
      z = Math.round(z / opts.gridSize) * opts.gridSize;
    }

    // Check for duplicate nodes within tolerance
    const duplicateNode = this.findNearbyNode(x, y, z, opts.toleranceDistance!);
    if (duplicateNode) {
      return {
        success: false,
        message: `Node already exists at (${duplicateNode.x}, ${duplicateNode.y}, ${duplicateNode.z}) within tolerance of ${opts.toleranceDistance}mm`,
        duplicateElement: duplicateNode
      };
    }

    // Create new node
    const nodeId = this.generateNodeId();
    const newNode: Node3D = {
      id: nodeId,
      x: x,
      y: y,
      z: z,
      label: nodeId,
      isSelected: false,
      isHovered: false,
      supportType: opts.supportType!,
      restraints: {
        fx: opts.supportType !== 'free',
        fy: opts.supportType !== 'free',
        fz: opts.supportType !== 'free',
        mx: opts.supportType === 'fixed',
        my: opts.supportType === 'fixed',
        mz: opts.supportType === 'fixed'
      },
      visualState: 'normal'
    };

    this.nodes.set(nodeId, newNode);

    // Auto-connect to nearby nodes if enabled
    const autoConnections: Beam3D[] = [];
    if (opts.autoConnect) {
      const nearbyNodes = this.findNodesWithinDistance(x, y, z, opts.connectionDistance!);
      
      for (const nearbyNode of nearbyNodes) {
        if (nearbyNode.id !== nodeId) {
          const beam = this.createBeamBetweenNodes(
            nodeId, 
            nearbyNode.id,
            { preventDuplicates: true }
          );
          if (beam.success && beam.element) {
            autoConnections.push(beam.element as Beam3D);
          }
        }
      }
    }

    return {
      success: true,
      message: `Node ${nodeId} created at (${x}, ${y}, ${z})${autoConnections.length > 0 ? ` with ${autoConnections.length} auto-connections` : ''}`,
      element: newNode,
      autoConnections
    };
  }

  /**
   * Creates a beam between two nodes with duplicate checking
   */
  public createBeamBetweenNodes(
    startNodeId: string,
    endNodeId: string,
    options?: Partial<BeamCreationOptions>
  ): StructuralElementResult {
    const opts: BeamCreationOptions = {
      preventDuplicates: true,
      material: 'steel',
      section: 'W360X64',
      autoReleases: false,
      ...options
    };

    // Validate nodes exist
    const startNode = this.nodes.get(startNodeId);
    const endNode = this.nodes.get(endNodeId);

    if (!startNode || !endNode) {
      return {
        success: false,
        message: `Invalid node IDs: ${startNodeId}, ${endNodeId}`
      };
    }

    // Prevent beam between same node
    if (startNodeId === endNodeId) {
      return {
        success: false,
        message: 'Cannot create beam between the same node'
      };
    }

    // Check for duplicate beams if enabled
    if (opts.preventDuplicates) {
      const duplicateBeam = this.findBeamBetweenNodes(startNodeId, endNodeId);
      if (duplicateBeam) {
        return {
          success: false,
          message: `Beam already exists between nodes ${startNodeId} and ${endNodeId}`,
          duplicateElement: duplicateBeam
        };
      }
    }

    // Calculate beam properties
    const length = this.calculateDistance(startNode, endNode);

    // Create new beam
    const beamId = this.generateBeamId();
    const newBeam: Beam3D = {
      id: beamId,
      startNodeId,
      endNodeId,
      section: opts.section!,
      material: opts.material!,
      localAxes: {
        x: [1, 0, 0],
        y: [0, 1, 0],
        z: [0, 0, 1]
      },
      betaAngle: 0,
      releases: {
        start: { fx: false, fy: false, fz: false, mx: false, my: false, mz: false },
        end: { fx: false, fy: false, fz: false, mx: false, my: false, mz: false }
      },
      length,
      isSelected: false,
      isHovered: false,
      visualState: 'normal'
    };

    this.beams.set(beamId, newBeam);

    return {
      success: true,
      message: `Beam ${beamId} created between nodes ${startNodeId} and ${endNodeId} (length: ${length.toFixed(2)}mm)`,
      element: newBeam
    };
  }

  /**
   * Find intersecting beam for a given beam in 3D space
   */
  public findIntersectingBeam(newBeam: Beam3D): IntersectionPoint | null {
    // Convert newBeam start/end points to proper format if needed
    const beams = Array.from(this.beams.values());
    
    // Check each existing beam for intersection
    for (const existingBeam of beams) {
      // Skip if it's the same beam or connecting beams
      if (existingBeam.id === newBeam.id ||
          existingBeam.startNodeId === newBeam.startNodeId ||
          existingBeam.startNodeId === newBeam.endNodeId ||
          existingBeam.endNodeId === newBeam.startNodeId ||
          existingBeam.endNodeId === newBeam.endNodeId) {
        continue;
      }

      const intersection = detectBeamIntersection(existingBeam, newBeam, this.nodes);
      if (intersection) {
        return intersection;
      }
    }

    return null;
  }

  /**
   * Creates a node at the intersection point between two beams
   */
  public createNodeAtIntersection(intersection: IntersectionPoint): IntersectionNodeResult {
    // Create new node at intersection point
    const nodeResult = this.createNodeAt(
      intersection.point.x,
      intersection.point.y,
      intersection.point.z,
      { autoConnect: false }
    );

    if (!nodeResult.success || !nodeResult.element) {
      return {
        success: false,
        message: 'Failed to create intersection node',
        nodeId: '',
        beamId: '',
        newBeams: [],
        removedBeamId: ''
      };
    }

    // Get the beams to split
    const beam1 = this.beams.get(intersection.beam1Id);
    const beam2 = this.beams.get(intersection.beam2Id);

    if (!beam1 || !beam2) {
      this.removeNode(nodeResult.element.id);
      return {
        success: false,
        message: 'Beam not found',
        nodeId: '',
        beamId: '',
        newBeams: [],
        removedBeamId: ''
      };
    }

    // Create new beams for the first intersecting beam
    const newBeam1Result = this.createBeamBetweenNodes(
      beam1.startNodeId,
      nodeResult.element.id,
      {
        material: beam1.material,
        section: beam1.section
      }
    );

    const newBeam2Result = this.createBeamBetweenNodes(
      nodeResult.element.id,
      beam1.endNodeId,
      {
        material: beam1.material,
        section: beam1.section
      }
    );

    // Create new beams for the second intersecting beam
    const newBeam3Result = this.createBeamBetweenNodes(
      beam2.startNodeId,
      nodeResult.element.id,
      {
        material: beam2.material,
        section: beam2.section
      }
    );

    const newBeam4Result = this.createBeamBetweenNodes(
      nodeResult.element.id,
      beam2.endNodeId,
      {
        material: beam2.material,
        section: beam2.section
      }
    );

    // Check if all beams were created successfully
    if (!newBeam1Result.success || !newBeam2Result.success ||
        !newBeam3Result.success || !newBeam4Result.success ||
        !newBeam1Result.element || !newBeam2Result.element ||
        !newBeam3Result.element || !newBeam4Result.element) {
      
      // Cleanup if any beam creation fails
      [newBeam1Result, newBeam2Result, newBeam3Result, newBeam4Result].forEach(result => {
        if (result.success && result.element) {
          this.removeBeam(result.element.id);
        }
      });
      this.removeNode(nodeResult.element.id);
      
      return {
        success: false,
        message: 'Failed to create intersection beams',
        nodeId: '',
        beamId: '',
        newBeams: [],
        removedBeamId: ''
      };
    }

    // Remove the original beams
    this.removeBeam(beam1.id);
    this.removeBeam(beam2.id);

    // Get all new beams
    const newBeams = [
      newBeam1Result.element,
      newBeam2Result.element,
      newBeam3Result.element,
      newBeam4Result.element
    ].filter((beam): beam is Beam3D => beam !== undefined);

    return {
      success: true,
      message: 'Created intersection node and updated beams',
      nodeId: nodeResult.element.id,
      beamId: newBeam1Result.element.id,
      newBeams: newBeams,
      removedBeamId: beam1.id
    };
  }

  /**
   * Creates a node at the intersection point between two beams (alternative version)
   */
  public createNodeAtIntersectionAlt(intersection: IntersectionPoint): StructuralElementResult {
    // Create new node at intersection point
    const nodeId = this.generateNodeId();
    const newNode: Node3D = {
      id: nodeId,
      x: intersection.point.x,
      y: intersection.point.y,
      z: intersection.point.z,
      label: nodeId,
      isSelected: false,
      isHovered: false,
      supportType: 'free',
      restraints: {
        fx: false,
        fy: false,
        fz: false,
        mx: false,
        my: false,
        mz: false
      },
      visualState: 'normal'
    };

    this.nodes.set(nodeId, newNode);

    // Get the beams to split
    const beam1 = this.beams.get(intersection.beam1Id);
    const beam2 = this.beams.get(intersection.beam2Id);

    if (!beam1 || !beam2) {
      this.removeNode(nodeId);
      return {
        success: false,
        message: 'Beam not found',
        nodeId: '',
        beamId: '',
        element: null
      };
    }

    // Create new beams for the first intersecting beam
    const newBeam1Result = this.createBeamBetweenNodes(
      beam1.startNodeId,
      nodeId,
      {
        material: beam1.material,
        section: beam1.section
      }
    );

    const newBeam2Result = this.createBeamBetweenNodes(
      nodeId,
      beam1.endNodeId,
      {
        material: beam1.material,
        section: beam1.section
      }
    );

    // Create new beams for the second intersecting beam
    const newBeam3Result = this.createBeamBetweenNodes(
      beam2.startNodeId,
      nodeId,
      {
        material: beam2.material,
        section: beam2.section
      }
    );

    const newBeam4Result = this.createBeamBetweenNodes(
      nodeId,
      beam2.endNodeId,
      {
        material: beam2.material,
        section: beam2.section
      }
    );

    // Check if all beams were created successfully
    if (!newBeam1Result.success || !newBeam2Result.success ||
        !newBeam3Result.success || !newBeam4Result.success ||
        !newBeam1Result.element || !newBeam2Result.element ||
        !newBeam3Result.element || !newBeam4Result.element) {
      
      // Cleanup if any beam creation fails
      [newBeam1Result, newBeam2Result, newBeam3Result, newBeam4Result].forEach(result => {
        if (result.success && result.element) {
          this.removeBeam(result.element.id);
        }
      });
      this.removeNode(nodeId);
      
      return {
        success: false,
        message: 'Failed to create intersection beams',
        nodeId: '',
        beamId: '',
        element: null
      };
    }

    // Remove the original beams
    this.removeBeam(beam1.id);
    this.removeBeam(beam2.id);

    return {
      success: true,
      message: 'Created intersection node and updated beams',
      nodeId: nodeId,
      beamId: newBeam1Result.element.id,
      element: newNode
    };
  }

  /**
   * Finds a node within tolerance distance of given coordinates
   */
  private findNearbyNode(x: number, y: number, z: number, tolerance: number): Node3D | null {
    for (const node of this.nodes.values()) {
      const distance = Math.sqrt(
        Math.pow(node.x - x, 2) +
        Math.pow(node.y - y, 2) +
        Math.pow(node.z - z, 2)
      );
      if (distance <= tolerance) {
        return node;
      }
    }
    return null;
  }

  /**
   * Finds all nodes within a specified distance
   */
  private findNodesWithinDistance(x: number, y: number, z: number, maxDistance: number): Node3D[] {
    const nearbyNodes: Node3D[] = [];
    
    for (const node of this.nodes.values()) {
      const distance = Math.sqrt(
        Math.pow(node.x - x, 2) +
        Math.pow(node.y - y, 2) +
        Math.pow(node.z - z, 2)
      );
      if (distance <= maxDistance && distance > 0) { // Exclude the exact same position
        nearbyNodes.push(node);
      }
    }
    
    return nearbyNodes.sort((a, b) => {
      const distA = this.calculateDistance3D(x, y, z, a.x, a.y, a.z);
      const distB = this.calculateDistance3D(x, y, z, b.x, b.y, b.z);
      return distA - distB;
    });
  }

  /**
   * Finds if a beam already exists between two nodes (in either direction)
   */
  private findBeamBetweenNodes(nodeId1: string, nodeId2: string): Beam3D | null {
    for (const beam of this.beams.values()) {
      if ((beam.startNodeId === nodeId1 && beam.endNodeId === nodeId2) ||
          (beam.startNodeId === nodeId2 && beam.endNodeId === nodeId1)) {
        return beam;
      }
    }
    return null;
  }

  /**
   * Calculates distance between two nodes
   */
  private calculateDistance(node1: Node3D, node2: Node3D): number {
    return Math.sqrt(
      Math.pow(node2.x - node1.x, 2) +
      Math.pow(node2.y - node1.y, 2) +
      Math.pow(node2.z - node1.z, 2)
    );
  }

  /**
   * Calculates distance between two 3D points
   */
  private calculateDistance3D(x1: number, y1: number, z1: number, x2: number, y2: number, z2: number): number {
    return Math.sqrt(
      Math.pow(x2 - x1, 2) +
      Math.pow(y2 - y1, 2) +
      Math.pow(z2 - z1, 2)
    );
  }

  /**
   * Generates a unique node ID
   */
  private generateNodeId(): string {
    this.nodeCounter++;
    return `N${this.nodeCounter.toString().padStart(3, '0')}`;
  }

  /**
   * Generates a unique beam ID
   */
  private generateBeamId(): string {
    this.beamCounter++;
    return `B${this.beamCounter.toString().padStart(3, '0')}`;
  }

  /**
   * Gets all current nodes as an array
   */
  public getNodes(): Node3D[] {
    return Array.from(this.nodes.values());
  }

  /**
   * Gets all current beams as an array
   */
  public getBeams(): Beam3D[] {
    return Array.from(this.beams.values());
  }

  /**
   * Gets a node by ID
   */
  public getNode(nodeId: string): Node3D | null {
    return this.nodes.get(nodeId) || null;
  }

  /**
   * Gets a beam by ID
   */
  public getBeam(beamId: string): Beam3D | undefined {
    return this.beams.get(beamId);
  }

  /**
   * Removes a node and all connected beams
   */
  public removeNode(nodeId: string): StructuralElementResult {
    const node = this.nodes.get(nodeId);
    if (!node) {
      return {
        success: false,
        message: `Node ${nodeId} not found`
      };
    }

    // Find and remove all beams connected to this node
    const connectedBeams: string[] = [];
    for (const [beamId, beam] of this.beams.entries()) {
      if (beam.startNodeId === nodeId || beam.endNodeId === nodeId) {
        connectedBeams.push(beamId);
      }
    }

    connectedBeams.forEach(beamId => this.beams.delete(beamId));
    this.nodes.delete(nodeId);

    return {
      success: true,
      message: `Node ${nodeId} removed along with ${connectedBeams.length} connected beams`
    };
  }

  /**
   * Removes a beam
   */
  public removeBeam(beamId: string): StructuralElementResult {
    const beam = this.beams.get(beamId);
    if (!beam) {
      return {
        success: false,
        message: `Beam ${beamId} not found`
      };
    }

    this.beams.delete(beamId);

    return {
      success: true,
      message: `Beam ${beamId} removed`
    };
  }

  /**
   * Finds all beams connected to a specific node
   */
  public getConnectedBeams(nodeId: string): Beam3D[] {
    const connectedBeams: Beam3D[] = [];
    for (const beam of this.beams.values()) {
      if (beam.startNodeId === nodeId || beam.endNodeId === nodeId) {
        connectedBeams.push(beam);
      }
    }
    return connectedBeams;
  }

  /**
   * Gets statistics about the current model
   */
  public getModelStatistics() {
    return {
      nodeCount: this.nodes.size,
      beamCount: this.beams.size,
      nextNodeId: `N${(this.nodeCounter + 1).toString().padStart(3, '0')}`,
      nextBeamId: `B${(this.beamCounter + 1).toString().padStart(3, '0')}`,
      totalLength: Array.from(this.beams.values()).reduce((sum, beam) => sum + (beam.length || 0), 0)
    };
  }

  /**
   * Validates the structural model for consistency
   */
  public validateModel(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Check for orphaned beams (beams referencing non-existent nodes)
    for (const beam of this.beams.values()) {
      if (!this.nodes.has(beam.startNodeId)) {
        errors.push(`Beam ${beam.id} references non-existent start node ${beam.startNodeId}`);
      }
      if (!this.nodes.has(beam.endNodeId)) {
        errors.push(`Beam ${beam.id} references non-existent end node ${beam.endNodeId}`);
      }
    }

    // Check for duplicate node IDs
    const nodeIds = new Set<string>();
    for (const node of this.nodes.values()) {
      if (nodeIds.has(node.id)) {
        errors.push(`Duplicate node ID found: ${node.id}`);
      }
      nodeIds.add(node.id);
    }

    // Check for duplicate beam IDs
    const beamIds = new Set<string>();
    for (const beam of this.beams.values()) {
      if (beamIds.has(beam.id)) {
        errors.push(`Duplicate beam ID found: ${beam.id}`);
      }
      beamIds.add(beam.id);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Clears all nodes and beams
   */
  public clear(): void {
    this.nodes.clear();
    this.beams.clear();
    this.nodeCounter = 0;
    this.beamCounter = 0;
  }

  /**
   * Get a map of all nodes in the model
   */
  public getNodesMap(): Map<string, Node3D> {
    return this.nodes;
  }

  /**
   * Get a map of all beams in the model
   */
  public getBeamsMap(): Map<string, Beam3D> {
    return this.beams;
  }
}

// Export a singleton instance for global use
export const structuralElementManager = new StructuralElementManager();

// Helper functions for coordinate manipulation
export const CoordinateUtils = {
  /**
   * Snaps coordinates to grid
   */
  snapToGrid(x: number, y: number, z: number, gridSize: number): { x: number; y: number; z: number } {
    return {
      x: Math.round(x / gridSize) * gridSize,
      y: Math.round(y / gridSize) * gridSize,
      z: Math.round(z / gridSize) * gridSize
    };
  },

  /**
   * Converts THREE.Vector3 to coordinate object
   */
  fromVector3(vector: THREE.Vector3): { x: number; y: number; z: number } {
    return {
      x: vector.x,
      y: vector.y,
      z: vector.z
    };
  },

  /**
   * Converts coordinate object to THREE.Vector3
   */
  toVector3(coords: { x: number; y: number; z: number }): THREE.Vector3 {
    return new THREE.Vector3(coords.x, coords.y, coords.z);
  },

  /**
   * Rounds coordinates to specified decimal places
   */
  roundCoordinates(x: number, y: number, z: number, decimals: number = 2): { x: number; y: number; z: number } {
    const factor = Math.pow(10, decimals);
    return {
      x: Math.round(x * factor) / factor,
      y: Math.round(y * factor) / factor,
      z: Math.round(z * factor) / factor
    };
  }
};
